import React, { useEffect, useState } from 'react';
import settingsService from '../../services/settings.service';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getError } from '../../utils';

const AdminInfo = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [_settingsId, setSettingsId] = useState(null);

  const [companyName, setCompanyName] = useState('');
  const [companySubname, setCompanySubname] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [image, setImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const settings = await settingsService.getPublicSettings();
        if (settings) {
          setSettingsId(settings._id);
          setCompanyName(settings.companyName || '');
          setCompanySubname(settings.companysubName || '');
          setCompanyAddress(settings.companyAddress || '');
          setCompanyPhone(settings.companyPhone || '');
          setCompanyCity(settings.companyCity || '');
          setBankAccount(settings.bankAccount || '');
          setImage(settings.image || null);
        }
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsService.updateSettings({
        companyName,
        companysubName: companySubname,
        companyAddress,
        companyPhone,
        companyCity,
        bankAccount,
        image,
      });
      toast.success(t('admin.companyUpdated'));
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  const uploadPicture = async event => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      let r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ') + r);
      if (image) formData.append('documentId', image._id);
      const response = await settingsService.uploadSettingsImg(formData);
      setImage(response.data || response);
      toast.success(t('admin.imageAdded'));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className='mt-2 max-w-lg'>
      <form>
        <div className='space-y-1.5 mb-3'>
          <label className='block text-sm font-medium text-foreground'>
            {t('admin.companyName')}
          </label>
          <Input value={companyName} onChange={e => setCompanyName(e.target.value)} />
        </div>

        <div className='space-y-1.5 mb-3'>
          <label className='block text-sm font-medium text-foreground'>
            {t('admin.companySlogan')}
          </label>
          <Input value={companySubname} onChange={e => setCompanySubname(e.target.value)} />
        </div>

        <div className='space-y-1.5 mb-3'>
          <label className='block text-sm font-medium text-foreground'>
            {t('admin.companyAddress')}
          </label>
          <Input value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} />
        </div>

        <div className='space-y-1.5 mb-3'>
          <label className='block text-sm font-medium text-foreground'>
            {t('admin.cityCountry')}
          </label>
          <Input value={companyCity} onChange={e => setCompanyCity(e.target.value)} />
        </div>

        <div className='space-y-1.5 mb-3'>
          <label className='block text-sm font-medium text-foreground'>{t('admin.phone')}</label>
          <Input value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} />
        </div>

        <div className='space-y-1.5 mb-3'>
          <label className='block text-sm font-medium text-foreground'>
            {t('admin.bankAccount')}
          </label>
          <textarea
            className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 resize-y'
            rows={3}
            value={bankAccount}
            onChange={e => setBankAccount(e.target.value)}
          />
        </div>

        <div className='flex items-start gap-6 mb-4'>
          <div className='flex-1 space-y-1.5'>
            <label className='block text-sm font-medium text-foreground'>
              {t('admin.uploadLogo')}
            </label>
            <input
              type='file'
              name='logo'
              onChange={uploadPicture}
              className='block w-full text-sm text-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer'
            />
          </div>
          <div className='mt-6'>
            <img
              alt={t('admin.logoPhoto')}
              src={image?.fileLink || '/images/emptyPhoto.png'}
              style={{ maxWidth: '100px', maxHeight: '100px' }}
            />
          </div>
        </div>

        <div>
          <Button type='button' onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                {t('common.loading')}
              </>
            ) : (
              t('auth.update')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminInfo;
